import React, { useEffect, useRef } from 'react';
import { useLang } from '../hooks/useLanguage';

import {
    createMapWithControls,
    createVectorLayerAndSource,
    zoomToFloorplanExtent,
    featureFromItem,
    reverseScale,
} from '../../../admin/src/components/svgeditor/mapFunctions.js';

import IconMinus from '../icons/IconMinus';
import IconPlus from '../icons/IconPlus';
import IconZero from '../icons/IconZero';
import IconSmallSeat from '../icons/IconSmallSeat';
import {
    SELECTED_SEAT_COLOR,
    SUGGESTED_SEAT_COLOR,
    DIMMED_SEAT_OPACITY,
} from '../helpers/colorMap';

function SvgViewer({
    elements,
    colorMap,
    onClickElement,
    inCartSeatIds,
    ticketTypeColorMap,
    ticketTypeNameI18nMap,
    dimmedSeatIds,
    showLabel = false,
    setShowLabel,
    showSuggestedLegend = false,
}) {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const vectorSourceRef = useRef(null);
    const featureMapRef = useRef([]);
    const firstTimeElementsSetRef = useRef(false);
    const userInteractedRef = useRef(false);
    const onClickElementRef = useRef(onClickElement);
    const resizeObserverRef = useRef(null);
    const { language, t } = useLang();

    // Keep onClickElement ref up to date
    useEffect(() => {
        onClickElementRef.current = onClickElement;
    }, [onClickElement]);

    useEffect(() => {
        if (!mapRef.current) return;

        const { vectorSource, vectorLayer } = createVectorLayerAndSource();
        vectorSourceRef.current = vectorSource;

        let oldPos = {
            center: [400, 300],
            zoom: 17,
        };
        if (mapInstanceRef.current) {
            oldPos = {
                center: mapInstanceRef.current.getView().getCenter(),
                zoom: mapInstanceRef.current.getView().getZoom(),
            };
        }

        const map = createMapWithControls(mapRef.current, vectorLayer, [], {
            center: oldPos.center,
            zoom: oldPos.zoom,
            minZoom: 15, // Set minZoom in the view options
            maxZoom: 18, // Set maxZoom in the view options
        });

        mapInstanceRef.current = map;

        // Update icon scales dynamically when zoom changes
        map.getView().on('change:resolution', () => {
            const zoom = map.getView().getZoom();
            featureMapRef.current.forEach((feature) => {
                const currentStyle = feature.getStyle();
                const image = currentStyle.getImage();
                if (image) {
                    image.setScale(reverseScale(zoom, feature.originalItem.scale));
                    feature.setStyle(currentStyle);
                }
            });
        });

        // Track manual pan/zoom so resize doesn't snap back to fit-all
        const markInteracted = () => {
            userInteractedRef.current = true;
        };
        map.on('pointerdrag', markInteracted);
        const mapEl = mapRef.current;
        mapEl.addEventListener('wheel', markInteracted, { passive: true });

        // Add click handler for feature selection
        map.on('click', (evt) => {
            const feature = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);
            if (feature && typeof onClickElementRef.current === 'function') {
                onClickElementRef.current(feature.getId());
            }
        });

        map.setTarget(mapRef.current);

        // Set up ResizeObserver to handle container resize
        if (mapRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.updateSize();
                    if (
                        firstTimeElementsSetRef.current &&
                        !userInteractedRef.current &&
                        vectorSourceRef.current
                    ) {
                        zoomToFloorplanExtent(mapInstanceRef.current, vectorSourceRef.current);
                    }
                }
            });
            resizeObserverRef.current.observe(mapRef.current);
        }

        return () => {
            map.setTarget(undefined);
            mapEl.removeEventListener('wheel', markInteracted);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (!mapInstanceRef.current || !vectorSourceRef.current) return;

        featureMapRef.current = [];
        vectorSourceRef.current.clear();
        userInteractedRef.current = false;

        const zoom = mapInstanceRef.current.getView().getZoom();

        elements.forEach((item) => {
            let opacity = inCartSeatIds.includes(item.id) ? 0.5 : 1;
            if (dimmedSeatIds?.has(Number(item.id))) {
                // Free, but not selectable with what's in the cart: fade it and
                // keep the ticket type's colour, rather than repainting it.
                opacity = DIMMED_SEAT_OPACITY;
            }
            if (item.bookable === false) {
                opacity = 0.2; // make non-bookable items more transparent
            }
            const f = featureFromItem(
                item,
                language,
                zoom,
                onClickElement,
                {
                    color: colorMap[item.id] || 'white',
                    opacity,
                },
                colorMap[item.id] === SELECTED_SEAT_COLOR ? false : !showLabel, // remove labels flag
            );
            vectorSourceRef.current.addFeature(f);
            featureMapRef.current.push(f);
        });

        // if it's the first time, zoom to the extent of the features
        if (mapInstanceRef.current && elements.length > 0 && !firstTimeElementsSetRef.current) {
            firstTimeElementsSetRef.current = true;
            //console.log('Zooming to floorplan extent for the first time');
            setTimeout(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.updateSize();
                }
                zoomToFloorplanExtent(mapInstanceRef.current, vectorSourceRef.current);
            }, 300);
        }
    }, [elements, colorMap, dimmedSeatIds, onClickElement, inCartSeatIds, language, showLabel]);

    return (
        <div className="fireknife-booking-selector" style={{ position: 'relative' }}>
            <div ref={mapRef} style={{ width: '100%', aspectRatio: '1.25' }} />
            <div className="fireknife-booking-selector__btns">
                <button
                    className="fireknife-booking-selector__btn fireknife-btn fireknife-btn--default"
                    onClick={() => {
                        const view = mapInstanceRef.current.getView();
                        view.animate({ zoom: view.getZoom() + 1, duration: 300 });
                    }}
                >
                    <IconPlus className="fireknife-booking-selector__btn-icon" />
                </button>
                <button
                    className="fireknife-booking-selector__btn fireknife-btn fireknife-btn--default"
                    onClick={() => {
                        const view = mapInstanceRef.current.getView();
                        view.animate({ zoom: view.getZoom() - 1, duration: 300 });
                    }}
                >
                    <IconMinus className="fireknife-booking-selector__btn-icon" />
                </button>
                <button
                    className="fireknife-booking-selector__btn fireknife-btn fireknife-btn--default"
                    onClick={() => {
                        zoomToFloorplanExtent(mapInstanceRef.current, vectorSourceRef.current);
                    }}
                >
                    <IconZero className="fireknife-booking-selector__btn-icon" />
                </button>
                <button
                    className="fireknife-booking-selector__btn fireknife-btn fireknife-btn--default"
                    onClick={() => {
                        setShowLabel(!showLabel);
                    }}
                >
                    <IconSmallSeat className="fireknife-booking-selector__btn-icon" />
                </button>
            </div>
            {(ticketTypeColorMap && Object.keys(ticketTypeColorMap).length > 0) ||
            showSuggestedLegend ? (
                <div className="fireknife-booking-selector__legend">
                    {ticketTypeColorMap &&
                        Object.entries(ticketTypeColorMap).map(([type, color]) => (
                            <div key={type} className="fireknife-booking-selector__legend-item">
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 16,
                                        height: 16,
                                        border: '1px solid black',
                                        borderRadius: 2,
                                        background: color,
                                        margin: 8,
                                    }}
                                />
                                <span>{ticketTypeNameI18nMap[type][language] || type}</span>
                            </div>
                        ))}
                    {showSuggestedLegend && (
                        <div className="fireknife-booking-selector__legend-item">
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 16,
                                    height: 16,
                                    border: '1px solid black',
                                    borderRadius: 2,
                                    background: SUGGESTED_SEAT_COLOR,
                                    margin: 8,
                                }}
                            />
                            <span>{t('widgetInfo.suggested')}</span>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
}

export default SvgViewer;
